import { toZonedTime as utcToZonedTime } from 'date-fns-tz/toZonedTime';
import { areIntervalsOverlapping } from 'date-fns/areIntervalsOverlapping';
import { endOfDay } from 'date-fns/endOfDay';
import { startOfDay } from 'date-fns/startOfDay';
import { IWarning } from 'redux-state/weather-warnings/types.js';
import { formatDate, TIMEZONE } from 'utilities/date.js';

const DEBUG = false;

type IntervalType = {
  start: Date;
  end: Date;
};

/** Converts date to interval */
export const dateConvertToInterval = (day: Date): IntervalType => {
  /**
   * Create Date Start End Period
   * As this will be different across timezones we set to utc
   * */
  const dateStartUTC = utcToZonedTime(startOfDay(day), TIMEZONE);
  const dateEndUTC = utcToZonedTime(endOfDay(day), TIMEZONE);
  const datePeriod = { start: dateStartUTC, end: dateEndUTC };
  return datePeriod;
};

/** Converts warning to interval */
export const warningConvertToInterval = (
  warning: Pick<IWarning, 'threatStartTime' | 'threatEndTime'>,
): IntervalType => {
  /** Create Warning Start End Period
   * As this will be different across timezones we set to utc
   */
  const threatStartTime = utcToZonedTime(warning.threatStartTime || new Date().toISOString(), TIMEZONE);
  const threatEndTime = utcToZonedTime(warning.threatEndTime || new Date().toISOString(), TIMEZONE);
  const warningPeriod = { start: threatStartTime, end: threatEndTime };
  return warningPeriod;
};

/** Returns warnings for that day period */
export const getWarningsForDay = (day: Date, warnings: IWarning[] = []): IWarning[] => {
  const datePeriod = dateConvertToInterval(day);
  const validWarnings = warnings.filter((warning) => {
    const warningPeriod = warningConvertToInterval(warning);
    return dateIntervalsOverlapping(datePeriod, warningPeriod);
  });
  return validWarnings;
};

/** Checks if two warnings overlap date set
 * Export for tests
 */
export const dateIntervalsOverlapping = (
  datePeriod: IntervalType,
  warningPeriod: IntervalType,
  debugArg = false,
): boolean => {
  /**
   * If the threat End Time is midnight then it will be included on the next day. So we reset the date - 1 millisecond
   * warningPeriod.end.setMilliseconds(warningPeriod.end.getMilliseconds() - 1);
   * Above is no longer used as we are using function areIntervalsOverlapping, adjacant value do not count, and setting option inclusive as false
   * https://date-fns.org/v2.29.3/docs/areIntervalsOverlapping
   */
  const intervalsOverlapping = areIntervalsOverlapping(datePeriod, warningPeriod, { inclusive: false });
  if (DEBUG || debugArg) {
    console.log(
      `
      Weather Dates
      DayStart: ${formatDate(datePeriod.start, `dd MMM yyyy hh:mma`)}
      DayEnd: ${formatDate(datePeriod.end, `dd MMM yyyy hh:mma`)}
      ThreatStart: ${formatDate(warningPeriod.start, `dd MMM yyyy hh:mma`)}
      ThreatEnd: ${formatDate(warningPeriod.end, `dd MMM yyyy hh:mma`)}
      IntervalsOverlapping: ${intervalsOverlapping}
      `,
    );
  }

  return intervalsOverlapping;
};
